import { useEffect, useState } from "react";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import { BUTTON_CREATE_USER, USERS_TITLE } from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { startGetAdsUsers } from "../../actions/user";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { getPathDatabaseByRole } from "../../actions/auth";
import { useNavigate } from "react-router-dom";
import UsersTable from "./components/UsersTable";
import Button from "../../components/Buttons/Button";
import Box from "@mui/material/Box";
import ModalUser from "./components/ModalUser";
import { ADVERTISER_ROLE, SUPER_ADMIN_ROLE } from "../../utils/constants";
import { Helmet } from "react-helmet";
import AddIcon from "@mui/icons-material/Add";
import useCheckAnonymous from "./hooks/useCheckAnonymous";
import { setShowModalSelectSubscription } from "../../actions/ui";

const Users = ({ embedded = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [modalUserOpen, setModalUserOpen] = useState(false);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const navigate = useNavigate();
  const { agencyID, role } = dispatch(getPathDatabaseByRole());
  const currentUserId = useSelectorApp((state) => state.auth?.user?.uid);
  const { theme } = useSelectorApp((state) => state.ui);
  const users = useSelectorApp((state) => state.user.users);
  const subscriptionPlanInfo = useSelectorApp(
    (state) => state.payments?.subscriptionPlanInfo
  );
  const { checkIsAnonymous } = useCheckAnonymous();

  const usersLimit = subscriptionPlanInfo?.benefits?.users;
  const currentUsersCount = Object.values(users || {}).filter(
    (user) => !user?.isDeleted
  ).length;
  const isCreateDisabled =
    usersLimit != null && currentUsersCount >= usersLimit;

  const getInitialData = async () => {
    if (!agencyID || (role !== ADVERTISER_ROLE && role !== SUPER_ADMIN_ROLE)) {
      navigate("/");
    }
    await dispatch(
      startGetAdsUsers({
        onCallbackLoadingFinish: () => setIsLoading(false),
      })
    );
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, []);

  const onAddUser = () => {
    if (checkIsAnonymous()) return;
    if (isCreateDisabled) {
      dispatch(setShowModalSelectSubscription(true));
      return;
    }
    setModalUserOpen(true);
  };

  const createUserButton = (
    <Button
      onClick={onAddUser}
      color={embedded ? "secondary" : "primary"}
      sx={{
        minWidth: 100,
        width: "auto",
      }}
    >
      {t(BUTTON_CREATE_USER)}
      <AddIcon
        sx={{
          color: embedded
            ? "inherit"
            : `${theme.palette.primary.contrastText} !important`,
          ml: 0.5,
          width: 20,
          height: 20,
        }}
      />
    </Button>
  );

  const usersContent = (
    <>
      {embedded ? (
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
        >
          {createUserButton}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            mt: 4.5,
            mb: 2,
          }}
        >
          <TitlePage>{t(USERS_TITLE)}</TitlePage>
          <span style={{ display: "inline-block" }}>{createUserButton}</span>
        </Box>
      )}
      <UsersTable
        data={users}
        loading={isLoading}
        currentUserId={currentUserId}
      />
      {modalUserOpen && (
        <ModalUser
          open={modalUserOpen}
          onCloseModal={() => setModalUserOpen(false)}
          mode="create"
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
        }}
      >
        {usersContent}
      </Box>
    );
  }

  return (
    <ContainerPage mode="padding" hasMarginBottom={true} sx={{ px: 5 }}>
      <Helmet>
        <title>{`${theme.nameBusiness} - Users`}</title>
      </Helmet>
      {usersContent}
    </ContainerPage>
  );
};

export default Users;
